import { useState, useCallback, useRef, useEffect } from "react";
import { DebitNoteDataEntity } from "../../domain/entities/DebitNoteEntity";
import { UpdateDebitNoteJsonUseCase } from "../../domain/use-cases/UpdateDebitNoteJsonUseCase";
import { DebitNoteRepositoryImpl } from "../../data/repositories/DebitNoteRepositoryImpl";
export type { DebitNoteDataEntity };

// Create repository and use case instances (singleton pattern)
const debitNoteRepository = new DebitNoteRepositoryImpl();
const updateDebitNoteJsonUseCase = new UpdateDebitNoteJsonUseCase(
  debitNoteRepository
);

const omitSupplyFields = (data: DebitNoteDataEntity): DebitNoteDataEntity => {
  const {
    place_of_supply: _placeOfSupply,
    destination_of_supply: _destinationOfSupply,
    source_of_supply: _sourceOfSupply,
    ...rest
  } = data;
  return rest;
};

// Default empty debit note structure for manual creation
const getDefaultDebitNoteJson = (): DebitNoteDataEntity => ({
  customer_id: "",
  customer_name: "",
  date: "",
  currency_id: "",
  currency_code: "",
  currency_symbol: "₹",
  line_items: [],
  sub_total: 0,
  tax_total: 0,
  total: 0,
  reference_number: "",
  notes: "",
  debitnote_number: "",
  gst_treatment: "business_gst",
  gst_no: "",
  is_reverse_charge_applied: false,
  reason_code: "",
  narration: "",
  linked_invoice_id: "",
});

export const useDebitNoteReview = (
  documentId: string | null,
  initialDebitNoteJson: DebitNoteDataEntity | null,
  disableAutoSave: boolean = false,
  shouldBlockSave?: () => boolean,
  onSaveBlocked?: () => void
) => {
  // Initialize with default structure if null (for manual creation)
  const [debitNoteJson, setDebitNoteJson] = useState<DebitNoteDataEntity>(
    initialDebitNoteJson || getDefaultDebitNoteJson()
  );
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedDataRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const maxRetries = 3;

  const debouncedSave = useCallback(
    async (debitNoteData: DebitNoteDataEntity, isRetry = false) => {
      if (!documentId) {
        console.error("No documentId to save");
        return;
      }

      if (shouldBlockSave?.()) {
        onSaveBlocked?.();
        return;
      }

      const savePayload = omitSupplyFields(debitNoteData);
      const currentDataString = JSON.stringify(savePayload);
      if (lastSavedDataRef.current === currentDataString && !isRetry) {
        return;
      }

      try {
        setIsAutoSaving(true);
        setSaveError(null);

        // Use the use case to update debit note JSON
        const result = await updateDebitNoteJsonUseCase.execute(
          parseInt(documentId),
          savePayload
        );

        if (!result.success) {
          throw new Error(result.error || "Failed to save changes");
        }

        setLastSaved(new Date());
        lastSavedDataRef.current = currentDataString;
        retryCountRef.current = 0;
      } catch (error) {
        console.error("Failed to save debit note JSON:", error);
        const errorMessage =
          error instanceof Error ? error.message : "Failed to save changes";
        setSaveError(errorMessage);

        if (retryCountRef.current < maxRetries && !isRetry) {
          retryCountRef.current++;
          setTimeout(() => {
            debouncedSave(debitNoteData, true);
          }, 2000 * retryCountRef.current);
        }
      } finally {
        setIsAutoSaving(false);
      }
    },
    [documentId, maxRetries, shouldBlockSave, onSaveBlocked]
  );

  const triggerAutoSave = useCallback(
    (debitNoteData: DebitNoteDataEntity) => {
      // Clear existing timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout for debounced save
      debounceTimeoutRef.current = setTimeout(() => {
        debouncedSave(debitNoteData);
      }, 1000); // 1 second debounce
    },
    [debouncedSave]
  );

  const updateDebitNoteJson = useCallback(
    (updates: Partial<DebitNoteDataEntity>) => {
      setDebitNoteJson((prev: DebitNoteDataEntity) => {
        const updated = { ...prev, ...updates };
        // Only auto-save if documentId exists and auto-save is not disabled
        // Auto-save is disabled for "Posted to Zoho" documents
        if (documentId && !disableAutoSave) {
          triggerAutoSave(updated);
        }
        return updated;
      });
    },
    [triggerAutoSave, documentId, disableAutoSave]
  );

  useEffect(() => {
    if (initialDebitNoteJson) {
      lastSavedDataRef.current = JSON.stringify(initialDebitNoteJson);
    } else {
      // For manual creation, initialize with default structure
      const defaultJson = getDefaultDebitNoteJson();
      setDebitNoteJson(defaultJson);
      // Don't track changes for new manual creation
      lastSavedDataRef.current = null;
    }
  }, [initialDebitNoteJson]);

  // Clear any pending auto-save operations when auto-save is disabled
  useEffect(() => {
    if (disableAutoSave) {
      // Clear any pending debounced save
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
      // Reset auto-saving state if it was in progress
      setIsAutoSaving(false);
    }
  }, [disableAutoSave]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return {
    debitNoteJson,
    setDebitNoteJson,
    isAutoSaving,
    lastSaved,
    saveError,
    updateDebitNoteJson,
    debouncedSave,
  };
};
